import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ChevronDown, ExternalLink } from "lucide-react";
import type { LandscapePaper, LandscapeCamp } from "@/types/landscape";
import { ROLE_COLORS } from "@/lib/viz-colors";
import { readingLabel } from "@/lib/reading-labels";
import { useLandscapeStore } from "@/stores/useLandscapeStore";
import { summarizeClusters } from "@/lib/landscape-clusters";

const ROLE_LABELS: Record<string, string> = {
  supports: "Supports",
  challenges: "Challenges",
  extends: "Extends",
  applies: "Applies",
  operationalises: "Operationalises",
  reviews: "Reviews",
};

// Per-viewer layout memory (app-level, like the orientation flag).
const WIDTH_KEY = "paperchase-landscape-sidebar-width";
const COLLAPSED_KEY = "paperchase-landscape-collapsed";
const DEFAULT_WIDTH = 300;
const MIN_WIDTH = 260;

function clampSidebarWidth(w: number, viewport: number): number {
  return Math.round(Math.min(Math.max(w, MIN_WIDTH), Math.max(MIN_WIDTH, viewport * 0.5)));
}

function readStored<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

function writeStored(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore storage failures — layout just isn't remembered
  }
}

function Section({
  id,
  title,
  collapsed,
  onToggle,
  children,
}: {
  id: string;
  title: string;
  collapsed: boolean;
  onToggle: (id: string) => void;
  children: ReactNode;
}) {
  return (
    <div className="border-b border-gray-200 dark:border-gray-700 px-4 py-3">
      <button
        type="button"
        onClick={() => onToggle(id)}
        aria-expanded={!collapsed}
        className="flex w-full items-center justify-between text-[10px] font-medium uppercase tracking-wider text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
      >
        {title}
        <ChevronDown
          className={`size-3.5 transition-transform ${collapsed ? "-rotate-90" : ""}`}
        />
      </button>
      {!collapsed && <div className="mt-2">{children}</div>}
    </div>
  );
}

interface Props {
  conceptId: string;
  conceptName: string;
  papers: LandscapePaper[];
  camps: LandscapeCamp[];
  thesis: { x: number; y: number } | null;
  onZoomToCamp: (campId: string) => void;
  /** Zoom the canvas to a topic cluster's centroid (data-space x/y). */
  onZoomToRegion: (x: number, y: number) => void;
  /**
   * Active papers in the concept (pre-SPECTER2 / pre-top-500 filter).
   * When > papers.length the header shows "N of M placed" plus a tooltip
   * so users can reconcile the dot count with the Analyze paper list.
   */
  candidateTotal?: number;
}

export function LandscapeSidebar({
  conceptId,
  conceptName,
  papers,
  camps,
  thesis,
  onZoomToCamp,
  onZoomToRegion,
  candidateTotal,
}: Props) {
  const navigate = useNavigate();
  const close = useLandscapeStore((s) => s.close);
  const hoveredId = useLandscapeStore((s) => s.hoveredId);
  const selectedId = useLandscapeStore((s) => s.selectedId);
  const visibleRoles = useLandscapeStore((s) => s.visibleRoles);
  const toggleRole = useLandscapeStore((s) => s.toggleRole);
  const colorBy = useLandscapeStore((s) => s.colorBy);
  const setColorBy = useLandscapeStore((s) => s.setColorBy);

  const clusters = useMemo(() => summarizeClusters(papers), [papers]);
  const hasRealClusters = useMemo(
    () => clusters.some((c) => c.id >= 0),
    [clusters],
  );

  // Guard: if a prior concept left colorBy="cluster" but this corpus has no
  // clusters, every dot would paint one Unclustered color. Fall back to role.
  useEffect(() => {
    if (colorBy === "cluster" && !hasRealClusters) setColorBy("role");
  }, [colorBy, hasRealClusters, setColorBy]);

  const dispPaper = useMemo(() => {
    const id = selectedId ?? hoveredId;
    return id ? papers.find((p) => p.paper_id === id) ?? null : null;
  }, [papers, hoveredId, selectedId]);

  const roleCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const r of Object.keys(ROLE_LABELS)) c[r] = 0;
    for (const p of papers) {
      if (p.argumentation_role in c) c[p.argumentation_role]++;
    }
    return c;
  }, [papers]);

  const campCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const camp of camps) c[camp.id] = 0;
    for (const p of papers) {
      if (p.camp_id && p.camp_id in c) c[p.camp_id]++;
    }
    return c;
  }, [papers, camps]);

  const bridgeCount = useMemo(
    () => papers.filter((p) => p.is_bridge).length,
    [papers],
  );

  const campForPaper = useMemo(() => {
    const m = new Map<string, string>();
    for (const camp of camps) m.set(camp.id, camp.label);
    return m;
  }, [camps]);

  const [collapsed, setCollapsed] = useState<string[]>(() => readStored(COLLAPSED_KEY, []));
  const toggleSection = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      writeStored(COLLAPSED_KEY, next);
      return next;
    });
  }, []);

  const [width, setWidth] = useState(() =>
    clampSidebarWidth(readStored(WIDTH_KEY, DEFAULT_WIDTH), window.innerWidth),
  );
  const drag = useRef<{ startX: number; startW: number } | null>(null);
  const setAndStoreWidth = useCallback((w: number) => {
    const next = clampSidebarWidth(w, window.innerWidth);
    setWidth(next);
    writeStored(WIDTH_KEY, next);
  }, []);

  return (
    <div
      data-testid="landscape-sidebar"
      style={{ width }}
      className="relative flex shrink-0 flex-col border-l border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800"
    >
      {/* Resize handle: drag the left edge (or arrow keys) to widen. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize panel"
        aria-valuenow={width}
        tabIndex={0}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          drag.current = { startX: e.clientX, startW: width };
        }}
        onPointerMove={(e) => {
          if (drag.current) {
            setWidth(clampSidebarWidth(drag.current.startW + drag.current.startX - e.clientX, window.innerWidth));
          }
        }}
        onPointerUp={(e) => {
          if (drag.current) setAndStoreWidth(drag.current.startW + drag.current.startX - e.clientX);
          drag.current = null;
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") setAndStoreWidth(width + 24);
          if (e.key === "ArrowRight") setAndStoreWidth(width - 24);
        }}
        className="absolute inset-y-0 -left-1 z-10 w-2 cursor-col-resize touch-none hover:bg-purple-400/40 focus-visible:bg-purple-400/40 focus-visible:outline-none"
      />

      {/* Header */}
      <div className="shrink-0 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-gray-400">
          Literature Landscape
        </div>
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 leading-snug">
          {conceptName}
        </div>
        <div className="mt-1 text-xs text-gray-500">
          {candidateTotal !== undefined && candidateTotal > papers.length ? (
            <span
              title={
                `${candidateTotal - papers.length} active paper(s) couldn't be placed: ` +
                `usually missing the SPECTER2 embedding (recent additions, ` +
                `or papers we couldn't resolve to Semantic Scholar). The map ` +
                `is also capped at the top 500 by score.`
              }
            >
              {papers.length} of {candidateTotal} placed &middot; {bridgeCount} bridges
            </span>
          ) : (
            <>{papers.length} papers &middot; {bridgeCount} bridges</>
          )}
        </div>
      </div>

      {/* Filters scroll on their own; the paper detail below keeps its space.
          (It used to be the last flex-1 item, and its overflow-auto let flexbox
          shrink it to its padding on ~900px-tall windows.) */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Section id="color" title="Color by" collapsed={collapsed.includes("color")} onToggle={toggleSection}>
          <div className="flex rounded-md border border-gray-200 p-0.5 dark:border-gray-600">
            {([
              { key: "role", label: "Role" },
              { key: "cluster", label: "Topic cluster" },
            ] as const).map((opt) => {
              const disabled = opt.key === "cluster" && !hasRealClusters;
              const active = colorBy === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  disabled={disabled}
                  onClick={() => setColorBy(opt.key)}
                  title={
                    disabled ? "Run clustering to color by topic" : undefined
                  }
                  className={`flex-1 rounded px-2 py-1 text-[11px] font-medium transition-colors ${
                    active
                      ? "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300"
                      : disabled
                        ? "text-gray-300 dark:text-gray-600"
                        : "text-gray-500 hover:bg-white dark:hover:bg-gray-700"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {!hasRealClusters && (
            <p className="mt-1.5 text-[10px] leading-relaxed text-gray-400">
              Run clustering on this concept to color the map by topic.
            </p>
          )}
        </Section>

        <Section id="roles" title="Argumentation role" collapsed={collapsed.includes("roles")} onToggle={toggleSection}>
          {Object.entries(ROLE_LABELS).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => toggleRole(key)}
              className={`flex w-full items-center justify-between rounded px-1.5 py-1 text-left transition-colors ${
                visibleRoles.has(key)
                  ? "bg-white dark:bg-gray-700"
                  : "opacity-50"
              }`}
            >
              <span className="flex items-center gap-2">
                <span
                  className="inline-block size-2 shrink-0 rounded-full"
                  style={{ background: ROLE_COLORS[key] }}
                />
                <span className="text-xs text-gray-800 dark:text-gray-200">{label}</span>
              </span>
              <span className="font-mono text-[11px] text-gray-400">
                {roleCounts[key] ?? 0}
              </span>
            </button>
          ))}
        </Section>

        <Section id="camps" title="Debate camps" collapsed={collapsed.includes("camps")} onToggle={toggleSection}>
          {camps.map((camp) => (
            <button
              key={camp.id}
              type="button"
              onClick={() => onZoomToCamp(camp.id)}
              className="flex w-full items-center gap-2 rounded px-1 py-1.5 text-left hover:bg-white dark:hover:bg-gray-700"
            >
              <div
                className="h-6 w-[3px] shrink-0 rounded-sm"
                style={{ background: camp.color, opacity: 0.7 }}
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-medium text-gray-800 dark:text-gray-200" title={camp.label}>
                  {camp.label}
                </div>
                <div className="text-[10px] text-gray-400">
                  {campCounts[camp.id] ?? 0} papers
                </div>
              </div>
            </button>
          ))}
        </Section>

        {hasRealClusters && (
          <Section id="clusters" title="Topic clusters" collapsed={collapsed.includes("clusters")} onToggle={toggleSection}>
            {clusters.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => onZoomToRegion(c.centroid.x, c.centroid.y)}
                title="Zoom to this cluster"
                className="flex w-full items-center gap-2 rounded px-1 py-1.5 text-left hover:bg-white dark:hover:bg-gray-700"
              >
                <span
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ background: c.color }}
                />
                <span className="min-w-0 flex-1 truncate text-xs text-gray-800 dark:text-gray-200">
                  {c.label}
                </span>
                <span className="shrink-0 font-mono text-[11px] text-gray-400">
                  {c.count}
                </span>
                {c.avgScore > 0 && (
                  <span className="shrink-0 text-[10px] text-gray-400">
                    avg {c.avgScore}
                  </span>
                )}
              </button>
            ))}
          </Section>
        )}

        {/* Thesis beacon */}
        {thesis && (
          <div className="border-b border-gray-200 dark:border-gray-700 px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="size-2.5 shrink-0 rounded-full bg-amber-600" />
              <span className="text-xs font-medium text-gray-800 dark:text-gray-200">
                Your argument
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Paper detail: pinned under the filters, never squeezed to nothing. */}
      <div
        data-testid="landscape-paper-detail"
        className="max-h-[55%] shrink-0 overflow-y-auto border-t border-gray-200 px-4 py-3 dark:border-gray-700"
      >
        {dispPaper ? (
          <>
            <div className="mb-2 text-[10px] font-medium uppercase tracking-wider text-gray-400">
              {selectedId ? "Selected" : "Hovered"}
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 p-3">
              <div
                className="mb-1 text-[10px] font-medium uppercase tracking-wider"
                style={{ color: ROLE_COLORS[dispPaper.argumentation_role] }}
              >
                {dispPaper.argumentation_role}
                {dispPaper.is_bridge ? " \u00b7 bridge" : ""}
              </div>
              <div className="mb-1.5 text-sm leading-snug text-gray-900 dark:text-gray-100">
                {dispPaper.title}
              </div>
              <div className="mb-1.5 text-xs text-gray-500">
                {dispPaper.authors}
              </div>
              <div className="mb-2 flex flex-wrap gap-x-2 gap-y-0.5 text-xs">
                <span className="text-gray-500">{dispPaper.year}</span>
                <span className="text-gray-500">
                  {dispPaper.citation_count} cites
                </span>
                <span className="font-medium text-gray-800 dark:text-gray-200">
                  Score {dispPaper.normalized_score}
                </span>
              </div>
              <div className="flex gap-1.5">
                <span
                  className="rounded px-1.5 py-0.5 text-[10px]"
                  style={{
                    background:
                      dispPaper.reading_status === "read"
                        ? "#dcfce7"
                        : dispPaper.reading_status === "skimmed"
                          ? "#dbeafe"
                          : "#f3f4f6",
                    color:
                      dispPaper.reading_status === "read"
                        ? "#166534"
                        : dispPaper.reading_status === "skimmed"
                          ? "#1e40af"
                          : "#6b7280",
                  }}
                >
                  {readingLabel(dispPaper.reading_status)}
                </span>
                {dispPaper.camp_id && campForPaper.has(dispPaper.camp_id) && (
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">
                    {campForPaper.get(dispPaper.camp_id)}
                  </span>
                )}
              </div>
              {selectedId && (
                <button
                  type="button"
                  onClick={() => {
                    sessionStorage.setItem("paperchase-landscape-return", conceptId);
                    sessionStorage.setItem("paperchase-landscape-paper", dispPaper.paper_id);
                    close();
                    navigate({
                      to: "/concepts/$conceptId/curate",
                      params: { conceptId },
                    });
                  }}
                  className="mt-2.5 flex w-full items-center justify-center gap-1.5 rounded bg-purple-100 px-2 py-1.5 text-xs font-medium text-purple-700 hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/50"
                >
                  <ExternalLink className="size-3" />
                  Open in Expand
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="py-3 text-center text-[11px] leading-relaxed text-gray-400">
            Hover or click any paper to see details.
          </div>
        )}
      </div>
    </div>
  );
}
